/**
 * Broadcast Service
 * Handles sending broadcasts to multiple leads with filter support
 */
import {
    db,
} from './firebase.config';
import {
    collection,
    addDoc,
    doc,
    getDoc,
    updateDoc,
    writeBatch,
    serverTimestamp,
    Timestamp,
} from 'firebase/firestore';
import { httpsCallable, getFunctions } from 'firebase/functions';
import { parseBroadcastTemplate } from '../utils/inboxHelpers';
import { uploadAttachments } from '../utils/uploadAttachments';
import { sanitizeAttachments } from '../utils/sanitizeAttachments';
import { buildMessagePayload } from '../utils/buildMessagePayload';

/**
 * Broadcast status values
 */
export const BROADCAST_STATUS = {
    PENDING: 'pending',
    SENDING: 'sending',
    SENT: 'sent',
    PARTIALLY_SENT: 'partially_sent',
    FAILED: 'failed',
    SCHEDULED: 'scheduled',
};

/**
 * Create a broadcast record in Firebase
 * 
 * @param {string} userId - User ID sending the broadcast
 * @param {Object} broadcastData - Broadcast configuration
 * @returns {Promise<string>} - Broadcast document ID
 */
export const createBroadcastRecord = async (userId, broadcastData) => {
    if (!userId) throw new Error('userId is required');

    const broadcastsRef = collection(db, 'broadcasts');

    const broadcastDoc = {
        userId,
        message: broadcastData.message || '',
        channel: broadcastData.channel || 'SMS',
        filters: {
            status: broadcastData.filters?.status || 'All',
            tags: broadcastData.filters?.tags || [],
        },
        recipientCount: broadcastData.recipientCount || 0,
        recipientIds: broadcastData.recipientIds || [],
        status: BROADCAST_STATUS.PENDING,
        successCount: 0,
        failedCount: 0,
        failedRecipients: [],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        completedAt: null,
    };

    const docRef = await addDoc(broadcastsRef, broadcastDoc);
    console.log('[broadcast.service] Broadcast record created:', docRef.id);

    return docRef.id;
};

/**
 * Update broadcast status
 * 
 * @param {string} broadcastId - Broadcast document ID
 * @param {Object} updates - Fields to update
 */
export const updateBroadcastStatus = async (broadcastId, updates) => {
    if (!broadcastId) throw new Error('broadcastId is required');

    const broadcastRef = doc(db, 'broadcasts', broadcastId);
    await updateDoc(broadcastRef, {
        ...updates,
        updatedAt: serverTimestamp(),
    });
};

/**
 * Get or create a conversation for a lead
 * Returns existing conversation or creates new one
 * 
 * @param {string} userId - User ID
 * @param {Object} lead - Lead object
 * @returns {Promise<Object>} - Conversation object with id
 */
const getOrCreateConversationForBroadcast = async (userId, lead) => {
    // Import dynamically to avoid circular dependency
    const { getOrCreateConversation } = await import('./conversation.service');
    return getOrCreateConversation(userId, lead);
};

/**
 * Send a single broadcast message - DELIVERY FIRST, then save
 * CRITICAL: Message is only saved to Firebase AFTER delivery is confirmed
 * If delivery fails, no data is written - the lead is simply skipped
 * 
 * @param {string} conversationId - Conversation ID
 * @param {Object} messageData - Message data
 * @param {Object} lead - Lead data for contact info
 * @returns {Promise<Object>} - Result with success status
 */
const sendSingleBroadcastMessage = async (conversationId, messageData, lead, userId) => {
    try {
        const conversationRef = doc(db, `conversations/${conversationId}`);

        // Get conversation details for email/phone
        const conversationSnap = await getDoc(conversationRef);
        const conversationData = conversationSnap.data() || {};

        // Get contact info
        const email = lead.email || conversationData.email;
        const phone = lead.phone || conversationData.phone;

        // STEP 1: Attempt delivery FIRST via Cloud Function
        let deliverySuccessful = false;

        if (messageData.channel === 'EMAIL') {
            if (!email) {
                // No email on file - skip this lead entirely
                console.warn('[broadcast.service] No email for lead:', lead.id);
                return { success: false, leadId: lead.id, error: 'No email address' };
            }

            try {
                const functions = getFunctions();
                const sendEmail = httpsCallable(functions, 'sendEmail');
                // Build canonical payload for backend
                const idempotencyKey = `${messageData.broadcastId || 'broadcast'}:${lead.id}:${Date.now()}`;
                await sendEmail({
                    to: email,
                    subject: messageData.subject || 'New message',
                    text: messageData.text,
                    html: messageData.html || null,
                    attachments: messageData.attachments || [],
                    conversationId: conversationId, // For thread tracking
                    idempotencyKey,
                });
                deliverySuccessful = true;
            } catch (emailError) {
                console.error('[broadcast.service] Email delivery failed for lead:', lead.id, emailError);
                // DELIVERY FAILED - Do NOT save anything, skip this lead
                return { success: false, leadId: lead.id, error: emailError.message };
            }
        } else if (messageData.channel === 'SMS') {
            if (!phone) {
                // No phone on file - skip this lead entirely
                console.warn('[broadcast.service] No phone for lead:', lead.id);
                return { success: false, leadId: lead.id, error: 'No phone number' };
            }

            try {
                const functions = getFunctions();
                const sendSms = httpsCallable(functions, 'sendSms');
                await sendSms({
                    to: phone,
                    message: messageData.text,
                });
                deliverySuccessful = true;
            } catch (smsError) {
                console.error('[broadcast.service] SMS delivery failed for lead:', lead.id, smsError);
                // DELIVERY FAILED - Do NOT save anything, skip this lead
                return { success: false, leadId: lead.id, error: smsError.message };
            }
        } else {
            // Invalid channel
            return { success: false, leadId: lead.id, error: 'Invalid channel' };
        }

        // STEP 2: Only save to Firebase AFTER delivery is confirmed
        if (deliverySuccessful) {
            const messagesRef = collection(db, `conversations/${conversationId}/messages`);

            // Build Firestore-safe message payload using strict contract
            const newMessage = buildMessagePayload({
                text: messageData.text,
                channel: messageData.channel,
                isSender: true,
                isBroadcast: true,
                broadcastId: messageData.broadcastId,
                subject: messageData.subject,
                attachments: messageData.attachments,
                timestamp: new Date().toISOString(),
            });

            console.log('[broadcast.service] DEBUG - messageData.attachments:', messageData.attachments);
            console.log('[broadcast.service] DEBUG - newMessage.attachments:', newMessage.attachments);
            console.log('[broadcast.service] Saving message with attachments:', newMessage.attachments);

            // HARD GUARD: Ensure attachments is never undefined if present
            if ('attachments' in newMessage && newMessage.attachments === undefined) {
                throw new Error(`Invalid payload: attachments is undefined for lead ${lead.id}`);
            }

            // Deep-validate payload to ensure no undefined values anywhere
            const assertNoUndefined = (obj, path = '') => {
                if (obj === undefined) throw new Error(`Undefined value at ${path || 'root'}`);
                if (obj === null) return;
                if (Array.isArray(obj)) {
                    obj.forEach((item, idx) => assertNoUndefined(item, `${path}[${idx}]`));
                    return;
                }
                if (typeof obj === 'object') {
                    Object.keys(obj).forEach(key => {
                        const val = obj[key];
                        if (val === undefined) throw new Error(`Undefined value at ${path ? path + '.' : ''}${key}`);
                        assertNoUndefined(val, `${path ? path + '.' : ''}${key}`);
                    });
                }
            };

            try {
                assertNoUndefined(newMessage);
            } catch (err) {
                console.error('[broadcast.service] Payload validation failed for lead:', lead.id, err.message, { newMessage });
                throw err;
            }

            await addDoc(messagesRef, newMessage);

            // Update conversation metadata
            // NOTE: Broadcast messages are outbound (isSender: true), so they don't increment unread
            // Unread is only for inbound messages from the contact
            await updateDoc(conversationRef, {
                lastMessage: messageData.text,
                lastUpdated: new Date().toISOString(),
                lastMessageAt: new Date().toISOString(),
            });

            return { success: true, conversationId, leadId: lead.id };
        }

        // Should not reach here, but return failure if somehow we do
        return { success: false, leadId: lead.id, error: 'Unknown delivery failure' };
    } catch (error) {
        console.error('[broadcast.service] Error in broadcast message flow for lead:', lead.id, error);
        // On any error, return failure - do NOT save anything
        return { success: false, conversationId, leadId: lead.id, error: error.message };
    }
};

/**
 * Send broadcast to multiple leads
 * CRITICAL: Only creates conversations and messages for SUCCESSFUL deliveries
 * Failed deliveries are skipped entirely - no Firebase data is written
 * 
 * @param {string} userId - User ID
 * @param {Array} leads - Array of lead objects to send to
 * @param {string} messageTemplate - Message template with variables
 * @param {string} channel - 'SMS' or 'EMAIL'
 * @param {Object} userData - User data for variable replacement
 * @param {Function} onProgress - Optional callback for progress updates
 * @returns {Promise<Object>} - Results with success/failure counts
 */
export const sendBroadcast = async (userId, leads, messageTemplate, channel, userData = {}, options = {}, onProgress = null) => {
    if (!userId) throw new Error('userId is required');
    if (!leads || leads.length === 0) throw new Error('No recipients provided');
    if (!messageTemplate) throw new Error('Message is required');

    const results = {
        total: leads.length,
        successful: 0,
        failed: 0,
        failedLeads: [],
        broadcastId: null,
    };

    // Pre-filter leads that have the required contact info for the channel
    const eligibleLeads = leads.filter(lead => {
        if (channel === 'EMAIL') return !!lead.email;
        if (channel === 'SMS') return !!lead.phone;
        return false;
    });

    const skippedLeads = leads.filter(lead => {
        if (channel === 'EMAIL') return !lead.email;
        if (channel === 'SMS') return !lead.phone;
        return true;
    });

    // Add skipped leads to failed results immediately
    skippedLeads.forEach(lead => {
        results.failed++;
        results.failedLeads.push({
            leadId: lead.id,
            error: `No ${channel === 'EMAIL' ? 'email' : 'phone'} on file`,
        });
    });

    // If no eligible leads, return early
    if (eligibleLeads.length === 0) {
        return results;
    }

    try {
        // Only create broadcast record if we have eligible leads
        const broadcastId = await createBroadcastRecord(userId, {
            message: messageTemplate,
            channel,
            recipientCount: eligibleLeads.length,
            recipientIds: eligibleLeads.map(l => l.id),
            filters: {},
        });
        results.broadcastId = broadcastId;

        // Normalize attachments to ensure they're always an array
        const normalizedAttachments = Array.isArray(options.attachments) ? options.attachments : [];
        options.attachments = normalizedAttachments;

        // If attachments are provided as File objects, upload them once under the broadcast scope
        if (options.attachments && Array.isArray(options.attachments) && options.attachments.length) {
            // detect File-like objects by presence of `name` and `size`
            const first = options.attachments[0];
            if (first && typeof first.name === 'string' && typeof first.size === 'number') {
                try {
                    const uploaded = await uploadAttachments({ files: options.attachments, userId, scope: 'broadcast', broadcastId: broadcastId, messageId: broadcastId });
                    options.attachments = uploaded;
                    // Persist attachments metadata to the broadcast document so cleanup trigger can find them
                    try {
                        await updateDoc(doc(db, 'broadcasts', broadcastId), { attachments: uploaded, updatedAt: serverTimestamp() });
                    } catch (persistErr) {
                        console.error('[broadcast.service] Failed to persist broadcast attachments to Firestore:', persistErr);
                        // Fail the broadcast early - do not attempt sending without Firestore ownership recorded
                        await updateBroadcastStatus(broadcastId, { status: BROADCAST_STATUS.FAILED, error: persistErr.message });
                        throw persistErr;
                    }
                } catch (uploadErr) {
                    console.error('[broadcast.service] Attachment upload failed for broadcast:', uploadErr);
                    // Fail the broadcast early - do not attempt sending without attachments being available
                    await updateBroadcastStatus(broadcastId, { status: BROADCAST_STATUS.FAILED, error: uploadErr.message });
                    throw uploadErr;
                }
            }
        }


        // --- Attachment pre-validation: ensure attachments are valid for all leads or abort ---
        // Normalize attachments to array if missing
        options.attachments = Array.isArray(options.attachments) ? options.attachments : [];

        // If any attachments were provided (UI or uploaded), sanitize and validate them now
        if (options.attachments.length > 0) {
            const originalCount = options.attachments.length;
            const sanitized = sanitizeAttachments(options.attachments);

            // If sanitization removed any attachments, abort the broadcast to avoid partial deliveries
            if (sanitized.length !== originalCount) {
                const errMsg = 'Invalid attachments: some attachments failed validation';
                console.error('[broadcast.service] ' + errMsg, { originalCount, sanitizedCount: sanitized.length });
                await updateBroadcastStatus(broadcastId, { status: BROADCAST_STATUS.FAILED, error: errMsg });
                throw new Error(errMsg);
            }

            // Replace attachments with sanitized, contract-compliant list
            options.attachments = sanitized;
        }
        // Update status to sending
        await updateBroadcastStatus(broadcastId, {
            status: BROADCAST_STATUS.SENDING,
        });

        // Process eligible leads in batches for better performance
        const BATCH_SIZE = 10;

        for (let i = 0; i < eligibleLeads.length; i += BATCH_SIZE) {
            const batch = eligibleLeads.slice(i, i + BATCH_SIZE);

            const batchPromises = batch.map(async (lead) => {
                try {
                    // Parse template variables for this lead
                    const personalizedMessage = parseBroadcastTemplate(messageTemplate, lead, userData);
                    const personalizedSubject = options.subject
                        ? parseBroadcastTemplate(options.subject, lead, userData)
                        : undefined;

                    // Attempt delivery FIRST before creating any conversation
                    // sendSingleBroadcastMessage will only create conversation/message if delivery succeeds

                    // Get or create conversation - but note this will only be used if delivery succeeds
                    const conversation = await getOrCreateConversationForBroadcast(userId, lead);

                    // Send the message (delivery-first approach)
                    // Pass userId for user-scoped reply-to address (CRITICAL for multi-user)
                    const result = await sendSingleBroadcastMessage(
                        conversation.id,
                        {
                            text: personalizedMessage,
                            channel,
                            broadcastId,
                            subject: personalizedSubject,
                            attachments: Array.isArray(options.attachments) ? options.attachments : [],
                        },
                        lead,
                        userId // Pass userId for user-scoped email routing
                    );

                    return result;
                } catch (error) {
                    console.error('[broadcast.service] Error processing lead:', lead.id, error);
                    return { success: false, leadId: lead.id, error: error.message };
                }
            });

            const batchResults = await Promise.all(batchPromises);

            // Aggregate results
            batchResults.forEach(result => {
                if (result.success) {
                    results.successful++;
                } else {
                    results.failed++;
                    results.failedLeads.push({
                        leadId: result.leadId,
                        error: result.error,
                    });
                }
            });

            // Progress callback - include skipped leads in total
            if (onProgress) {
                onProgress({
                    processed: skippedLeads.length + i + batch.length,
                    total: leads.length,
                    successful: results.successful,
                    failed: results.failed,
                });
            }
        }

        // Update final broadcast status - only count actually delivered messages
        const finalStatus = results.successful === 0
            ? BROADCAST_STATUS.FAILED
            : results.failed === 0
                ? BROADCAST_STATUS.SENT
                : BROADCAST_STATUS.PARTIALLY_SENT;

        await updateBroadcastStatus(broadcastId, {
            status: finalStatus,
            successCount: results.successful,
            failedCount: results.failed,
            failedRecipients: results.failedLeads,
            completedAt: serverTimestamp(),
        });

        console.log('[broadcast.service] Broadcast completed:', {
            broadcastId,
            status: finalStatus,
            successful: results.successful,
            failed: results.failed,
        });

        return results;
    } catch (error) {
        console.error('[broadcast.service] Broadcast failed:', error);

        if (results.broadcastId) {
            await updateBroadcastStatus(results.broadcastId, {
                status: BROADCAST_STATUS.FAILED,
                error: error.message,
            });
        }

        throw error;
    }
};

/**
 * Get filtered leads based on status and tags
 * Reuses the same filtering logic as Leads module
 * 
 * @param {Array} leads - All leads
 * @param {string} status - Status filter ('All' or specific status)
 * @param {Array} tags - Tags to filter by (lead must have ALL tags)
 * @returns {Array} - Filtered leads
 */
export const filterLeadsForBroadcast = (leads, status = 'All', tags = []) => {
    let filtered = [...leads];

    // Filter by status
    if (status !== 'All') {
        filtered = filtered.filter(lead => lead.status === status);
    }

    // Filter by tags (lead must have ALL selected tags)
    if (tags.length > 0) {
        filtered = filtered.filter(lead => {
            const leadTags = lead.tags || [];
            return tags.every(selectedTag =>
                leadTags.some(leadTag =>
                    leadTag.toLowerCase() === selectedTag.toLowerCase() ||
                    leadTag.toLowerCase() === selectedTag.toLowerCase().replace('#', '') ||
                    `#${leadTag}`.toLowerCase() === selectedTag.toLowerCase()
                )
            );
        });
    }

    return filtered;
};

/**
 * Validate broadcast before sending
 * 
 * @param {Array} leads - Recipients
 * @param {string} message - Message content
 * @param {string} channel - Channel type
 * @returns {Object} - { valid: boolean, errors: string[] }
 */
export const validateBroadcast = (leads, message, channel) => {
    const errors = [];

    if (!leads || leads.length === 0) {
        errors.push('No recipients selected');
    }

    if (!message || message.trim().length === 0) {
        errors.push('Message cannot be empty');
    }

    if (channel !== 'SMS' && channel !== 'EMAIL') {
        errors.push('Invalid channel selected');
    }

    // Check if recipients have contact info for the selected channel
    if (leads && leads.length > 0) {
        const missingContact = leads.filter(lead => {
            if (channel === 'SMS') {
                return !lead.phone;
            } else if (channel === 'EMAIL') {
                return !lead.email;
            }
            return false;
        });

        if (missingContact.length > 0 && missingContact.length === leads.length) {
            errors.push(`None of the selected leads have ${channel === 'SMS' ? 'phone numbers' : 'email addresses'}`);
        } else if (missingContact.length > 0) {
            // Warning, not error - some leads may be skipped
            console.warn(`[broadcast.service] ${missingContact.length} leads missing ${channel === 'SMS' ? 'phone' : 'email'}`);
        }
    }

    return {
        valid: errors.length === 0,
        errors,
    };
};
